/* eslint-disable no-restricted-syntax, no-await-in-loop, no-continue, no-promise-executor-return, consistent-return, no-void, max-len, class-methods-use-this, no-underscore-dangle, no-undef, no-plusplus, prefer-destructuring, no-param-reassign, no-use-before-define, no-nested-ternary */
// Ported from newdtrader (packages/core/src/_common/base/dfunded); kept in its
// original style so fixes can be carried across the two copies.
/**
 * dfunded/core.js
 *
 * Transport-agnostic DFunded funded-account adapter core.
 *
 * Ported from multi_platform_sites `src/services/dfunded.ts` (worktree
 * `dfunded-multi-platform-sites`, branch `feat/dfunded-fasttrader`) — that
 * module is battle-tested in production; the trading/streaming logic below is
 * unchanged from it. What differs here is only the transport-agnostic seam:
 *
 *  - No hostname-based mode gating (`isDFundedHostname`/`isDFundedAvailable`
 *    are gone) — the parent app decides when DFunded mode is active by
 *    appending `?api_version=dfunded` and seeding a session; see
 *    `isDFundedMode()` below.
 *  - No `loginDFunded()` — login happens in the parent (MPS); this module
 *    only ever reads/refreshes a session that already exists.
 *  - Events are delivered through an injected emitter (`setDFundedEmitter`)
 *    instead of the MPS `wrapperListeners` Set, so any transport (a
 *    WebSocket-compatible class, in Task 2 of this port) can consume them.
 *  - `wrapApiForDFunded` (the Deriv-request-shaped dispatch table) is NOT
 *    ported here — that dispatch lives in the transport wrapper built on top
 *    of this core (Task 2). This module only exports the underlying
 *    building blocks (requests, streaming, synthesis) it needs.
 *
 * @see .superpowers/sdd/task-1-brief.md
 */

const { DFundedError } = require('./errors');
const { DFundedGateway, GatewayError } = require('./gateway');
const { mergeTickStats, withTickStats } = require('./tick-stats');
const {
    closeDFundedPublicFeed,
    getDFundedPublicFeed,
    PublicFeedRejection,
    PublicFeedUnavailable,
    shapePublicRequest,
} = require('./public-feed');
const { normalizeV2Response } = require('../v2-websocket-wrapper');
const dynamicConfig = require('../../../config/dynamic-config');

const SESSION_KEY = 'dfunded_session_v1';
const DFUNDED_SESSION_EVENT = 'dfunded-session-changed';

// Direct socket to the executor; null until /api/dfunded/config advertises one.
let gateway = null;
const lastStreamedTickAt = new Map();
const TICK_STREAM_STALE_MS = 4000;
const isTransportFailure = error => error instanceof GatewayError && String(error.code).startsWith('GATEWAY_');

/**
 * @typedef {Object} DFundedAccount
 * @property {string} id
 * @property {number} starting_equity
 * @property {number} current_equity
 * @property {number} reserved_stake
 * @property {number} realized_pnl
 * @property {number} open_trade_count
 * @property {string} status
 * @property {{name: string, currency: string, max_stake: number, max_open_trades: number, stage?: string}|null} [dfunded_programs]
 */

/**
 * @typedef {Object} DFundedSession
 * @property {string} accessToken
 * @property {string} refreshToken
 * @property {number} [expiresAt]
 * @property {string} [email]
 * @property {DFundedAccount} [account]
 */

/**
 * @typedef {Object} DFundedOrder
 * @property {string} id
 * @property {'queued'|'processing'|'submitted'|'open'|'won'|'lost'|'rejected'|'execution_unknown'} status
 * @property {number|null} pnl
 * @property {number|null} [deriv_contract_id]
 * @property {number|null} [buy_price]
 * @property {number|null} [sell_price]
 * @property {number|null} [entry_tick]
 * @property {number|null} [exit_tick]
 * @property {number|null} [entry_tick_time]
 * @property {number|null} [exit_tick_time]
 * @property {string} [contract_type]
 * @property {string} [symbol]
 * @property {number} [duration]
 * @property {string} [duration_unit]
 * @property {string|null} [barrier]
 * @property {number} [stake]
 * @property {string|null} [error_code]
 * @property {string|null} [error_message]
 * @property {string|null} [sell_error]
 * @property {number|null} [payout]
 * @property {string|null} [longcode]
 * @property {string|null} [shortcode]
 * @property {number|null} [buy_transaction_id]
 * @property {number|null} [purchase_time]
 * @property {Record<string, any>|null} [contract_snapshot]
 * @property {Record<string, any>|null} [parameters]
 */

// ─── Emitter injection (replaces MPS `wrapperListeners` Set + `emitAll`) ────
// The transport wrapper built on top of this core (Task 2) registers itself
// here; every synthesized stream/response message flows through it.

let dfundedEmit = () => {};
const setDFundedEmitter = fn => {
    dfundedEmit = typeof fn === 'function' ? fn : () => {};
};
const emitAll = data => dfundedEmit(data);

/** Emit on a macrotask so the send() promise resolves (and callers store the
 * contract id from the response) BEFORE any synthesized stream message lands.
 * Real Deriv sockets also deliver the response before subscription events. */
const emitSoon = data => {
    setTimeout(() => emitAll(data), 0);
};

// ─── API base ────────────────────────────────────────────────────────────────

const getDFundedApiBase = () =>
    ((typeof window !== 'undefined' && window.__DFUNDED_API_BASE__) ||
        (typeof process !== 'undefined' && process.env && process.env.PLATFORM_API_BASE) ||
        'https://dforge.site').replace(/\/$/, '');

// ─── Session ─────────────────────────────────────────────────────────────────

const emitSession = () => window.dispatchEvent(new CustomEvent(DFUNDED_SESSION_EVENT));

/**
 * The site owner can switch DFunded off for a site (dashboard
 * `features.dfunded`). Off means no DFunded button and any session left in
 * storage is ignored, so the site trades on its normal Deriv connection.
 * A config without the key, or any error reading it, keeps DFunded on.
 */
const isDFundedOfferedOnSite = () => {
    try {
        return dynamicConfig.isDFundedEnabled() !== false;
    } catch {
        return true;
    }
};

const readStoredDFundedSession = () => {
    try {
        // localStorage keeps the funded session across reloads/tabs (same
        // lesson as the Deriv v2 session persistence fix). Migrate any older
        // sessionStorage-era value on first read.
        let value = localStorage.getItem(SESSION_KEY);
        if (!value) {
            value = sessionStorage.getItem(SESSION_KEY);
            if (value) {
                localStorage.setItem(SESSION_KEY, value);
                sessionStorage.removeItem(SESSION_KEY);
            }
        }
        return value ? JSON.parse(value) : null;
    } catch {
        return null;
    }
};

/** The signed-in DFunded session, or null (also when DFunded is switched off on this site). */
const getDFundedSession = () => (isDFundedOfferedOnSite() ? readStoredDFundedSession() : null);

const saveDFundedSession = session => {
    if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else {
        localStorage.removeItem(SESSION_KEY);
        closeDFundedPublicFeed();
    }
    sessionStorage.removeItem(SESSION_KEY);
    emitSession();
};

/** DFunded mode: a DFunded account is signed in on this site (the session
 * is the same localStorage key fasttrader.site and the other DForge sites
 * use, so one login covers all of them). */
/** The program name as dfunded.site shows it, e.g. "Core 250 Phase 1". */
const getDFundedProgramName = (account = (getDFundedSession() || {}).account) =>
    (account && account.dfunded_programs && account.dfunded_programs.name) || null;

/**
 * What kind of account this is, for the header: "Community Challenge",
 * "Phase 1", "Phase 2" or "Funded". The program's stage decides; sessions
 * saved before the API sent a stage fall back to the program name.
 */
const getDFundedAccountType = (account = (getDFundedSession() || {}).account) => {
    const program = account && account.dfunded_programs;
    if (!program) return null;
    const by_stage = { competition: 'Community Challenge', challenge: 'Phase 1', verification: 'Phase 2', funded: 'Funded' };
    if (program.stage && by_stage[program.stage]) return by_stage[program.stage];
    const name = program.name || '';
    if (/community/i.test(name)) return 'Community Challenge';
    if (/phase\s*1/i.test(name)) return 'Phase 1';
    if (/phase\s*2/i.test(name)) return 'Phase 2';
    if (/funded/i.test(name)) return 'Funded';
    return name || null;
};

const isDFundedMode = () => {
    if (typeof window === 'undefined' || !isDFundedOfferedOnSite()) return false;
    const session = getDFundedSession();
    return !!(session && session.account);
};

// Single-flight refresh: the account poll, market proxy and contract streams
// all hit 401 in the same instant when the access token expires. Supabase
// ROTATES refresh tokens, so concurrent refreshes with the same token make the
// losers look like token reuse and used to wipe the whole session. Everyone
// now awaits one shared refresh.
let refreshInFlight = null;

const refreshSession = session => {
    if (!refreshInFlight) {
        refreshInFlight = (async () => {
            try {
                const latest = getDFundedSession() ?? session;
                const response = await fetch(`${getDFundedApiBase()}/api/dfunded/auth/refresh`, {
                    method : 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body   : JSON.stringify({ refreshToken: latest.refreshToken }),
                });
                if (!response.ok) {
                    // Only a definitive auth rejection ends the session; a flaky
                    // network/server hiccup must not log the trader out.
                    if ([400, 401, 403].includes(response.status)) {
                        saveDFundedSession(null);
                        throw new Error('Your DFunded session expired. Please log in again.');
                    }
                    throw new Error('DFunded session refresh failed. Please retry.');
                }
                const auth = await response.json();
                const refreshed = { ...latest, ...auth };
                saveDFundedSession(refreshed);
                return refreshed;
            } finally {
                refreshInFlight = null;
            }
        })();
    }
    return refreshInFlight;
};

const dfundedRequest = async (path, init = {}, session = getDFundedSession(), canRefresh = true) => {
    const response = await fetch(`${getDFundedApiBase()}${path}`, {
        ...init,
        headers: {
            'Content-Type': 'application/json',
            ...(session?.accessToken ? { Authorization: `Bearer ${session.accessToken}` } : {}),
            ...init.headers,
        },
    });
    if (response.status === 401 && session?.refreshToken && canRefresh) {
        return dfundedRequest(path, init, await refreshSession(session), false);
    }
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new DFundedError(body?.error?.code || 'DFUNDED_REQUEST_FAILED', body?.error?.message);
    }
    return body;
};

/** Sign in with a DFunded Account ID and trading password; the token is kept only in the session. */
const loginDFunded = async (loginId, password) => {
    if (!isDFundedOfferedOnSite()) throw new DFundedError('DFUNDED_NOT_OFFERED', 'DFunded sign-in is not available on this site.');
    const id = String(loginId || '').trim().toUpperCase();
    const auth = await dfundedRequest('/api/dfunded/auth/login', {
        method: 'POST',
        body  : JSON.stringify({ loginId: id, password }),
    }, null);
    const session = { ...auth, account: undefined, loginId: (auth.account && auth.account.loginId) || id };
    const me = await dfundedRequest('/api/dfunded/me', {}, session);
    const account = (me.accounts || []).find(candidate => candidate.id === (auth.account && auth.account.id)) || (me.accounts || [])[0];
    if (!account) throw new DFundedError('ACCOUNT_NOT_FOUND', 'No DFunded trading account is assigned to this login.');
    session.account = account;
    saveDFundedSession(session);
    return session;
};

/** End the DFunded session here and, best effort, on the server. */
const logoutDFunded = () => {
    const session = getDFundedSession();
    saveDFundedSession(null);
    if (session && session.accessToken) {
        fetch(`${getDFundedApiBase()}/api/dfunded/auth/logout`, {
            method : 'POST',
            headers: { Authorization: `Bearer ${session.accessToken}` },
        }).catch(() => undefined);
    }
};

const refreshDFundedAccount = async () => {
    const session = getDFundedSession();
    if (!session) return null;
    const me = await dfundedRequest('/api/dfunded/me', {}, session);
    const latest = getDFundedSession();
    if (!latest || latest.refreshToken !== session.refreshToken) return null;
    // Stay on the account this session was opened for; never drift to another one.
    session.account = me.accounts?.find(candidate => candidate.id === session.account?.id) ?? me.accounts?.[0];
    saveDFundedSession(session);
    return session;
};

const placeDFundedTrade = async trade => {
    const session = getDFundedSession();
    if (!session?.account) throw new Error('Log in with DFunded before placing a funded trade.');
    // One id for every attempt: the server treats a repeat of it as the same
    // order, so falling back from the socket to REST can never buy twice.
    const clientOrderId = crypto.randomUUID();
    if (gateway?.isReady) {
        try {
            const order = await gateway.buy({ ...trade, clientOrderId });
            handleOrderPush(order);
            return { order };
        } catch (error) {
            // A server answer (risk rule, Deriv rejection) is final. Only a
            // transport failure is worth retrying over REST.
            if (!isTransportFailure(error)) throw error;
        }
    }
    return dfundedRequest(
        '/api/dfunded/trades',
        {
            method: 'POST',
            body  : JSON.stringify({ ...trade, accountId: session.account.id, clientOrderId }),
        },
        session
    );
};

const getDFundedOrder = async orderId => {
    const session = getDFundedSession();
    if (!session?.account) throw new Error('DFunded session expired.');
    return dfundedRequest(
        `/api/dfunded/trades?accountId=${encodeURIComponent(session.account.id)}&orderId=${encodeURIComponent(orderId)}`,
        {},
        session
    );
};

const getDFundedOrders = async status => {
    const session = getDFundedSession();
    if (!session?.account) throw new Error('DFunded session expired.');
    const statusQuery = status ? `&status=${encodeURIComponent(status)}` : '';
    return dfundedRequest(
        `/api/dfunded/trades?accountId=${encodeURIComponent(session.account.id)}${statusQuery}`,
        {},
        session
    );
};

const sellDFundedContract = async contractId => {
    const session = getDFundedSession();
    if (!session?.account) throw new Error('DFunded session expired.');
    let orderId = trackedContracts.get(contractId)?.orderId;
    let sentDirect = false;
    if (gateway?.isReady && orderId) {
        try {
            await gateway.sell(contractId);
            sentDirect = true;
        } catch (error) {
            // CONTRACT_NOT_FOUND only means this executor is not streaming it;
            // the REST path below can still queue the sell.
            if (!isTransportFailure(error) && !(error instanceof GatewayError && error.code === 'CONTRACT_NOT_FOUND')) {throw error;}
        }
    }
    if (!sentDirect) {
        const { order } = await dfundedRequest(
            '/api/dfunded/trades',
            {
                method: 'PATCH',
                body  : JSON.stringify({ accountId: session.account.id, contractId }),
            },
            session
        );
        orderId = order.id;
    }
    const settled = await waitForDFundedOrder(orderId, current => {
        if (current.sell_error) throw new Error(current.sell_error);
        return current.status === 'won' || current.status === 'lost';
    });
    return settled;
};

const terminalOrderStatuses = new Set(['won', 'lost', 'rejected', 'execution_unknown']);

const waitForDFundedOrder = async (orderId, predicate, timeoutMs = 120_000) => {
    const deadline = Date.now() + timeoutMs;
    let lastKnown = null;
    let lastRestFetch = 0;
    while (Date.now() < deadline) {
        // Push-first: executor broadcasts arrive in one hop; the REST fetch is
        // capped so a wait isn't a train of multi-hop Supabase round trips.
        const pushed = pushedOrders.get(orderId);
        let order = lastKnown ? withPushedOrder(orderId, lastKnown) : pushed || null;
        if (!order || (!predicate(order) && Date.now() - lastRestFetch > (pushReady() ? 2500 : 350))) {
            lastRestFetch = Date.now();
            const fetched = await getDFundedOrder(orderId);
            if (!fetched.order) throw new Error('Funded trade could not be found.');
            order = withPushedOrder(orderId, fetched.order);
            lastKnown = fetched.order;
        }
        if (order.status === 'rejected' || order.status === 'execution_unknown') {
            // Keep Deriv's own code and wording for the purchase panel.
            throw new DFundedError(
                order.error_code || 'DFundedExecutionError',
                order.error_message || (order.error_code ? undefined : 'Funded trade execution needs review.')
            );
        }
        if (predicate(order)) return order;
        await Promise.race([orderPushSignal(), new Promise(resolve => setTimeout(resolve, 100))]);
    }
    throw new Error('Funded trade timed out while waiting for the executor.');
};

// ─── Runtime mirroring helpers ──────────────────────────────────────────────

/** Latest raw Deriv contract state mirrored by the executor, if any. */
const orderSnapshot = order => {
    const raw = order.contract_snapshot ?? order.parameters?.__runtime?.snapshot ?? null;
    return raw ? normalizeV2Response(raw) : null;
};

/** Buy receipt (payout/longcode/transaction id) mirrored by the executor. */
const orderReceipt = order => {
    if (order.longcode !== undefined && order.longcode !== null) {
        return {
            payout            : order.payout,
            longcode          : order.longcode,
            shortcode         : order.shortcode,
            buy_transaction_id: order.buy_transaction_id,
            purchase_time     : order.purchase_time,
        };
    }
    return order.parameters?.__runtime?.receipt || {};
};

/**
 * The server reduces a stake above the per-trade maximum, or larger than the
 * loss room left, instead of refusing it; `stake_cap_reason` says which. Tell
 * the page that hosts this frame (it shows the DF toast on every tab) and
 * anything in this frame that listens.
 */
const noteStakeCap = order => {
    const requested = Number(order && order.requested_stake);
    const stake = Number(order && order.stake);
    if (typeof window === 'undefined' || !(requested > stake)) return;
    const cap = order.stake_cap_reason;
    const reason = cap === 'max_stake' || cap === 'loss_room' ? cap : null;
    const detail = { requested, stake, reason };
    try {
        window.dispatchEvent(new CustomEvent('dfunded-stake-capped', { detail }));
    } catch {
        /* no CustomEvent here */
    }
    if (window.parent && window.parent !== window) {
        // Iframe -> parent: no secrets in this message; the parent checks the origin.
        window.parent.postMessage({ type: 'newdtrader:dfunded-stake-capped', ...detail }, '*');
    }
};

const executeDFundedPurchase = async purchase => {
    const parameters = purchase.parameters || {};
    const stake = Number(purchase.price ?? parameters.amount);
    const contractType = String(parameters.contract_type || '');
    const durationUnit = String(parameters.duration_unit || 't');
    const { order } = await placeDFundedTrade({
        symbol  : String(parameters.symbol || parameters.underlying_symbol || ''),
        contractType,
        duration: Number(parameters.duration || 1),
        durationUnit,
        ...(parameters.barrier !== undefined ? { barrier: String(parameters.barrier) } : {}),
        stake,
        parameters,
    });
    noteStakeCap(order);

    const opened = await waitForDFundedOrder(
        order.id,
        current => current.status === 'open' || terminalOrderStatuses.has(current.status),
        30_000
    );
    if (!opened.deriv_contract_id) throw new Error('Funded executor did not return a Deriv contract ID.');

    const contractId = Number(opened.deriv_contract_id);
    const buyPrice = Number(opened.buy_price ?? opened.stake ?? stake);
    const receipt = orderReceipt(opened);
    const symbol = String(parameters.symbol || parameters.underlying_symbol || '');
    return {
        dfunded_order_id: opened.id,
        dfunded_order   : opened,
        buy             : {
            contract_id   : contractId,
            transaction_id: pinnedBuyTxnId(contractId, Number(receipt.buy_transaction_id ?? contractId)),
            buy_price     : buyPrice,
            payout        : Number(receipt.payout ?? 0) || buyPrice,
            currency      : fundedCurrency(),
            longcode      : String(receipt.longcode || `${contractType} contract on ${symbol}`),
            shortcode     : receipt.shortcode || undefined,
            purchase_time : Number(receipt.purchase_time ?? Math.floor(Date.now() / 1000)),
            start_time    : Number(receipt.purchase_time ?? Math.floor(Date.now() / 1000)),
        },
    };
};

const fundedCurrency = () => getDFundedSession()?.account?.dfunded_programs?.currency || 'USD';

const fundedAvailable = () => {
    const account = getDFundedSession()?.account;
    return Number(account?.current_equity ?? 0) - Number(account?.reserved_stake ?? 0);
};

const fundedLoginid = () => {
    const account = getDFundedSession()?.account;
    return account ? `DFUNDED-${account.id.slice(0, 8).toUpperCase()}` : 'DFUNDED';
};

const fundedBalancePayload = () => ({
    balance : Number(fundedAvailable().toFixed(2)),
    currency: fundedCurrency(),
    loginid : fundedLoginid(),
});

// ─── Shared adapter state (module scope so every consumer and the
// reload-hydration path see the same contracts/proposals) ───────────────────

const proposals = new Map();
const trackedContracts = new Map();
const proposalPollers = new Map();
const streamingOrders = new Set();
let hydrated = false;

// ─── Market-data proxy ──────────────────────────────────────────────────────
// Unauthenticated browser sockets get an EMPTY Deriv product catalog in many
// regions (active_symbols: [], Offerings* errors on contracts_for/proposal,
// trading_times failures) — only tick streams work without authorization.
// A normal login fixes that via `authorize`; a DFunded session has no Deriv
// token, so catalog and quote requests are answered by the backend over the
// authorized master connection instead.

const pipDecimalsBySymbol = new Map();

/**
 * Deriv's public socket answers catalog and quote requests without a login, so
 * ask it first: it is one hop, and it keeps this traffic off the funded
 * backend, whose Deriv connection is for orders. The backend stays as the
 * fallback for a browser the public socket will not serve.
 */
const publicMarketRequest = async req => {
    const shaped = shapePublicRequest(req);
    if (!shaped) return null;
    try {
        return await getDFundedPublicFeed().request(shaped);
    } catch (error) {
        if (error instanceof PublicFeedRejection) {
            // Offerings* is Deriv declining to serve this region, not a bad request.
            if (error.code.startsWith('Offerings')) return null;
            throw new GatewayError(error.code, error.message);
        }
        if (error instanceof PublicFeedUnavailable) return null;
        throw error;
    }
};

// Gateway answers that mean "no account-priced quote right now", as opposed to
// Deriv refusing the contract itself: the public price is used instead.
const QUOTE_FALLBACK_CODES = new Set([
    'QUOTE_BUSY',
    'QUOTE_UNAVAILABLE',
    'QUOTE_STREAM_LOST',
    'LIVE_MASTER_UNAVAILABLE',
    'RATE_LIMITED',
    'TOO_MANY_QUOTES',
    'INVALID_REQUEST',
    'AUTH_REQUIRED',
    'SESSION_EXPIRED',
    'ACCOUNT_MISMATCH',
    'ACCOUNT_NOT_FOUND',
    'INTERNAL_ERROR',
    'MARKET_REQUEST_NOT_ALLOWED',
]);
const isQuoteFallback = error =>
    !(error instanceof GatewayError) || QUOTE_FALLBACK_CODES.has(error.code) || String(error.code).startsWith('GATEWAY_');

const marketRequestRaw = async req => {
    // A quote is priced by the account and app that will buy it (the gateway);
    // Deriv's public price differs, so it is only the fallback.
    if (req?.proposal && gateway?.isReady) {
        try {
            return await gateway.market(req);
        } catch (error) {
            if (!isTransportFailure(error) && !isQuoteFallback(error)) throw error;
        }
    }
    const direct = await publicMarketRequest(req);
    if (direct) return direct;
    if (gateway?.isReady) {
        try {
            return await gateway.market(req);
        } catch (error) {
            if (!isTransportFailure(error)) throw error;
        }
    }
    const { response } = await dfundedRequest('/api/dfunded/market', {
        method: 'POST',
        body  : JSON.stringify({ request: req }),
    });
    return response;
};

const marketRequest = async req => {
    const response = await marketRequestRaw(req);
    const normalized = normalizeV2Response(response);
    if (Array.isArray(normalized?.active_symbols)) {
        for (const entry of normalized.active_symbols) {
            const pip = Number(entry.pip ?? entry.pip_size ?? 0);
            if (entry.symbol && pip > 0) {
                pipDecimalsBySymbol.set(String(entry.symbol), Math.max(0, Math.round(-Math.log10(pip))));
            }
        }
    }
    return normalized;
};

// ─── Tick-stream emulation ──────────────────────────────────────────────────
// Unauthenticated sockets in the affected regions reject STREAMING requests
// (`ticks_history` + subscribe → InvalidSymbol) while one-shot history works.
// A normal login fixes this via authorize; for DFunded we emulate the stream:
// answer the subscribe request with one-shot history, then poll for new epochs
// and emit Deriv-shaped `tick` messages through the shared onMessage stream.

// Dedicated raw WebSocket for tick polling. Deriv's ticks_history rate limit
// is PER CONNECTION, so polling on the app's long-lived sockets slowly poisons
// them for every other consumer (charts, analysis, engine). A dedicated socket
// isolates that budget, and reconnecting on RateLimit gets a fresh one.
let pollSocket = null;
let pollSocketReqId = 1;
const pollSocketPending = new Map();

const resetPollSocket = () => {
    try {
        pollSocket?.close();
    } catch {
        /* already closed */
    }
    pollSocket = null;
    for (const pending of pollSocketPending.values()) pending.reject(new Error('POLL_SOCKET_RESET'));
    pollSocketPending.clear();
};

const getPollSocket = () =>
    new Promise((resolve, reject) => {
        if (pollSocket && pollSocket.readyState === WebSocket.OPEN) return resolve(pollSocket);
        if (pollSocket && pollSocket.readyState === WebSocket.CONNECTING) {
            pollSocket.addEventListener('open', () => resolve(pollSocket), { once: true });
            pollSocket.addEventListener('error', () => reject(new Error('POLL_SOCKET_FAILED')), { once: true });
            return;
        }
        // v2 public market socket: the legacy v3 endpoint was retired by Deriv.
        const socket = new WebSocket('wss://api.derivws.com/trading/v1/options/ws/public');
        pollSocket = socket;
        socket.addEventListener('message', event => {
            try {
                const data = JSON.parse(String(event.data));
                const pending = pollSocketPending.get(Number(data.req_id));
                if (pending) {
                    pollSocketPending.delete(Number(data.req_id));
                    if (data.error) {
                        pending.reject(
                            Object.assign(new Error(data.error.message || data.error.code), { code: data.error.code })
                        );
                    } else pending.resolve(data);
                }
            } catch {
                /* non-JSON frame */
            }
        });
        socket.addEventListener('close', () => {
            if (pollSocket === socket) resetPollSocket();
        });
        socket.addEventListener('open', () => resolve(socket), { once: true });
        socket.addEventListener('error', () => reject(new Error('POLL_SOCKET_FAILED')), { once: true });
    });

const pollSocketSend = async payload => {
    const socket = await getPollSocket();
    const reqId = pollSocketReqId++;
    return new Promise((resolve, reject) => {
        pollSocketPending.set(reqId, { resolve, reject });
        setTimeout(() => {
            if (pollSocketPending.delete(reqId)) reject(new Error('POLL_SOCKET_TIMEOUT'));
        }, 10_000);
        socket.send(JSON.stringify({ ...payload, req_id: reqId }));
    });
};

const pollTicksHistory = async (symbol, count) => {
    try {
        return await pollSocketSend({ ticks_history: symbol, end: 'latest', count, style: 'ticks' });
    } catch (error) {
        // Per-connection budget exhausted → recycle the socket for a fresh one.
        if (error?.code === 'RateLimit') resetPollSocket();
        throw error;
    }
};

// ONE poller per symbol regardless of how many tools subscribe — repeated
// per-subscription pollers previously stacked up and tripped Deriv's
// ticks_history rate limit, killing every feed at once.
const symbolTickPollers = new Map();
const tickSubscriptionSymbols = new Map();

const pipDecimalsOf = (symbol, quotes) => {
    const known = pipDecimalsBySymbol.get(symbol);
    if (known !== undefined) return known;
    let decimals = 0;
    for (const quote of quotes.slice(-10)) {
        const text = String(quote);
        const dot = text.indexOf('.');
        if (dot >= 0) decimals = Math.max(decimals, text.length - dot - 1);
    }
    return decimals;
};

const stopTickPoller = subscriptionId => {
    const symbol = tickSubscriptionSymbols.get(subscriptionId);
    tickSubscriptionSymbols.delete(subscriptionId);
    if (!symbol) return;
    const poller = symbolTickPollers.get(symbol);
    if (!poller) return;
    poller.subscriptionIds.delete(subscriptionId);
    if (!poller.subscriptionIds.size) {
        clearInterval(poller.timer);
        poller.unwatchPublic();
        symbolTickPollers.delete(symbol);
        gateway?.unwatchTicks(symbol);
    }
};

const stopAllTickPollers = () => {
    for (const id of [...tickSubscriptionSymbols.keys()]) stopTickPoller(id);
};

// The chart, the trade panel and the digit stats each hold their OWN
// subscription to the same symbol, exactly as they would on a Deriv socket, so
// every subscriber gets its own copy under its own subscription id. Emitting
// only for the first id left the others (usually the chart) without prices.
const emitTick = (poller, symbol, fields) => {
    [...poller.subscriptionIds].forEach(streamId => {
        emitAll({
            msg_type    : 'tick',
            echo_req    : { ticks: symbol, subscribe: 1 },
            tick        : { id: streamId, symbol, ...fields },
            subscription: { id: streamId },
        });
    });
};

const joinTickStream = (subscriptionId, symbol, initialLastEpoch) => {
    tickSubscriptionSymbols.set(subscriptionId, symbol);
    const existing = symbolTickPollers.get(symbol);
    if (existing) {
        existing.subscriptionIds.add(subscriptionId);
        return;
    }
    // Real streamed ticks, straight from Deriv's public socket. If that goes
    // quiet the funded backend's stream is asked for, and the history poller
    // below is the last resort; each steps aside while ticks are arriving.
    const unwatchPublic = getDFundedPublicFeed().watchTicks(symbol, tick => handleStreamedTick(symbol, tick));
    const poller = {
        unwatchPublic,
        startedAt      : Date.now(),
        lastEpoch      : initialLastEpoch,
        subscriptionIds: new Set([subscriptionId]),
        backoffUntil   : 0,
        timer          : setInterval(async () => {
            if (!getDFundedSession()?.account) {
                for (const id of [...poller.subscriptionIds]) stopTickPoller(id);
                return;
            }
            if (Date.now() - Math.max(lastStreamedTickAt.get(symbol) || 0, poller.startedAt) < TICK_STREAM_STALE_MS) {return;}
            // The public stream is quiet: bring in the backend's, and poll until one of them delivers.
            gateway?.watchTicks(symbol);
            if (Date.now() < poller.backoffUntil) return;
            poller.backoffUntil = Date.now() + 1800; // serialize; released early below on success
            try {
                const response = await pollTicksHistory(symbol, 5);
                poller.backoffUntil = 0;
                const times = response?.history?.times || [];
                const prices = response?.history?.prices || [];
                const pipSize = pipDecimalsOf(symbol, prices);
                for (let index = 0; index < times.length; index++) {
                    const epoch = Number(times[index]);
                    if (epoch <= poller.lastEpoch) continue;
                    poller.lastEpoch = epoch;
                    emitTick(poller, symbol, { epoch, quote: Number(prices[index]), pip_size: pipSize });
                }
            } catch (error) {
                // Deriv RateLimit → cool off instead of hammering.
                const code = error?.code || error?.error?.code;
                poller.backoffUntil = Date.now() + (code === 'RateLimit' ? 5_000 : 3_000);
            }
        }, 1000),
    };
    symbolTickPollers.set(symbol, poller);
};

const stopProposalPoller = subscriptionId => {
    proposalPollers.get(subscriptionId)?.();
    proposalPollers.delete(subscriptionId);
};

const stopAllProposalPollers = () => {
    for (const id of [...proposalPollers.keys()]) stopProposalPoller(id);
};

// ─── Parked quote streams ───────────────────────────────────────────────────
// D-Trader forgets every price stream the instant a buy is confirmed and asks
// for the same quotes again a moment later; its buy buttons stay disabled
// until they arrive. Re-subscribing at Deriv cost a full round trip on every
// trade. A forgotten stream is now parked instead: it stays subscribed for a
// short grace, and an identical request within it is answered at once from
// the live stream. Nothing is parked for long, so Deriv sees no extra load.
const PROPOSAL_PARK_MS = 3000;
const proposalParkMs = () => {
    const override = typeof globalThis !== 'undefined' ? globalThis.__DFUNDED_PROPOSAL_PARK_MS__ : undefined;
    return typeof override === 'number' && override >= 0 ? override : PROPOSAL_PARK_MS;
};
const parkedProposalStreams = new Map();

/** The request as Deriv sees it, minus the fields that change per subscription. */
const proposalFingerprint = shaped =>
    JSON.stringify(
        Object.keys(shaped)
            .filter(key => !['req_id', 'passthrough', 'subscribe'].includes(key))
            .sort()
            .map(key => [key, shaped[key]])
    );

const parkProposalStream = holder => {
    holder.onUpdate = null;
    const previous = parkedProposalStreams.get(holder.fingerprint);
    if (previous && previous !== holder) {
        clearTimeout(previous.timer);
        previous.stop();
    }
    holder.timer = setTimeout(() => {
        if (parkedProposalStreams.get(holder.fingerprint) === holder) parkedProposalStreams.delete(holder.fingerprint);
        holder.stop();
    }, proposalParkMs());
    parkedProposalStreams.set(holder.fingerprint, holder);
};

const reclaimProposalStream = fingerprint => {
    const holder = parkedProposalStreams.get(fingerprint);
    if (!holder) return null;
    clearTimeout(holder.timer);
    parkedProposalStreams.delete(fingerprint);
    // A parked quote that has gone quiet is not trusted: the socket may have
    // dropped it, and a stale price is worse than one round trip.
    if (!holder.last || Date.now() - holder.lastAt > proposalParkMs()) {
        holder.stop();
        return null;
    }
    return holder;
};

/**
 * A streamed quote priced by the account and app the buy will use (over the
 * gateway), or Deriv's public price when that is not available.
 */
const openQuoteStream = async (shaped, onUpdate) => {
    if (gateway?.isReady) {
        try {
            return await gateway.streamQuote(shaped, onUpdate);
        } catch (error) {
            if (!isQuoteFallback(error)) throw error;
        }
    }
    return getDFundedPublicFeed().streamQuote(shaped, onUpdate);
};

/**
 * A subscribed quote as a real Deriv stream. Resolves to null when no socket
 * can serve it, and the caller polls the backend.
 */
const streamProposal = async req => {
    const shaped = shapePublicRequest({ ...req, subscribe: undefined });
    if (!shaped) return null;
    // The app keeps the id of the first quote (buy-by-id resolves through it),
    // so every later price is re-labelled with that id.
    let proposalId = null;
    const fingerprint = proposalFingerprint(shaped);
    try {
        let holder = reclaimProposalStream(fingerprint);
        let first;
        if (holder) {
            first = withTickStats(holder.last, holder.ticks);
        } else {
            holder = { fingerprint, last: null, lastAt: 0, ticks: null, onUpdate: null, proposalId: null, timer: null, stop: () => {} };
            const current = holder;
            let currentStop = () => {};
            let ended = false;
            let reopening = false;
            const receive = update => {
                if (update && update.error) {
                    // The stream ended (gateway reconnect, server budget): open
                    // another the same way, so prices keep coming.
                    if (ended || reopening) return;
                    reopening = true;
                    openQuoteStream(shaped, receive)
                        .then(next => {
                            reopening = false;
                            if (ended) return next.stop();
                            currentStop = next.stop;
                            receive(next.first);
                        })
                        .catch(() => {
                            reopening = false;
                        });
                    return;
                }
                current.last = update;
                current.lastAt = Date.now();
                current.ticks = mergeTickStats(current.ticks, update);
                if (current.onUpdate) current.onUpdate(update);
            };
            const stream = await openQuoteStream(shaped, receive);
            currentStop = stream.stop;
            holder.stop = () => {
                ended = true;
                currentStop();
            };
            first = stream.first;
            holder.last = first;
            holder.lastAt = Date.now();
            holder.ticks = mergeTickStats(null, first);
        }
        holder.onUpdate = update => {
            if (!proposalId || !getDFundedSession()?.account) return;
            const normalized = normalizeV2Response(update);
            if (!normalized?.proposal) return;
            emitAll({
                echo_req    : req,
                msg_type    : 'proposal',
                proposal    : { ...normalized.proposal, id: proposalId },
                subscription: { id: `dfunded-prop-${proposalId}` },
            });
        };
        const normalized = normalizeV2Response(first);
        // A reclaimed stream's latest price may not repeat the id; the stream's own id still stands.
        const id = normalized?.proposal ? String(normalized.proposal.id || holder.proposalId || '') : '';
        if (!id) {
            holder.stop();
            return normalized;
        }
        proposalId = id;
        holder.proposalId = id;
        const subscriptionId = `dfunded-prop-${proposalId}`;
        // The previous poller for this id, if any, is this same parked stream: replace, never park again.
        proposalPollers.delete(subscriptionId);
        proposalPollers.set(subscriptionId, () => parkProposalStream(holder));
        return { ...normalized, proposal: { ...normalized.proposal, id } };
    } catch (error) {
        if (error instanceof PublicFeedRejection && !error.code.startsWith('Offerings')) {
            throw new GatewayError(error.code, error.message);
        }
        // Deriv refused the contract on the account itself.
        if (error instanceof GatewayError && !String(error.code).startsWith('Offerings')) throw error;
        return null;
    }
};

const handleProposal = async req => {
    const streamed = req.subscribe ? await streamProposal(req) : null;
    const response = streamed || (await marketRequest(req));
    const proposal = response?.proposal;
    if (!proposal?.id) return { echo_req: req, msg_type: 'proposal', ...response };
    const proposalId = String(proposal.id);
    proposals.set(proposalId, { ...req });

    let subscription;
    if (req.subscribe) {
        const subscriptionId = `dfunded-prop-${proposalId}`;
        subscription = { id: subscriptionId };
        if (!streamed) {
            // The public socket could not stream it: refresh the quote through
            // the backend instead, keeping the ORIGINAL id so buy-by-id keeps
            // resolving. The backend shares one upstream quote a second between
            // identical requests, so this pace costs Deriv nothing extra.
            stopProposalPoller(subscriptionId);
            const timer = setInterval(async () => {
                if (!getDFundedSession()?.account) {
                    stopProposalPoller(subscriptionId);
                    return;
                }
                try {
                    const update = await marketRequest({ ...req, subscribe: undefined });
                    if (!update?.proposal) return;
                    emitAll({
                        echo_req    : req,
                        msg_type    : 'proposal',
                        proposal    : { ...update.proposal, id: proposalId },
                        subscription: { id: subscriptionId },
                    });
                } catch {
                    /* transient — next beat retries */
                }
            }, 2000);
            proposalPollers.set(subscriptionId, () => clearInterval(timer));
        }
    }

    // NOTE: the initial response is dispatched by the WRAPPER (which attaches
    // `req_id` so DerivAPIBasic can resolve its pending promise) — do not
    // emitSoon() it here too, or a subscribed proposal double-fires: once from
    // this emitter and once from the wrapper's dispatch of the returned
    // message. The quote stream above (emitAll with the pinned proposal id)
    // remains the sole source of stream updates.
    const message = { echo_req: req, msg_type: 'proposal', proposal, ...(subscription ? { subscription } : {}) };
    return message;
};

// ─── Realtime push (Supabase broadcast) ─────────────────────────────────────
// The executor broadcasts every order/account event on `dfunded-acct-{id}`.
// With pushes, the browser behaves like a real Deriv socket: contract updates,
// settlements and balance changes arrive in one hop instead of via REST polls
// that each cost multiple Supabase round trips. Polling stays as a fallback.

let realtimeReady = false;
let realtimeAccountId = null;
let realtimeCleanup = null;
const pushedOrders = new Map();
let orderPushResolvers = [];

const orderPushSignal = () =>
    new Promise(resolve => {
        orderPushResolvers.push(resolve);
    });
const wakeOrderWaiters = () => {
    const resolvers = orderPushResolvers;
    orderPushResolvers = [];
    for (const resolve of resolvers) resolve();
};

const handleOrderPush = payload => {
    if (!payload?.id) return;
    const merged = { ...(pushedOrders.get(String(payload.id)) || {}), ...payload };
    pushedOrders.set(String(payload.id), merged);
    if (pushedOrders.size > 200) {
        const firstKey = pushedOrders.keys().next().value;
        if (firstKey) pushedOrders.delete(firstKey);
    }
    wakeOrderWaiters();
};

const handleAccountPush = payload => {
    const session = getDFundedSession();
    if (!session?.account || String(payload?.id) !== session.account.id) return;
    session.account = { ...session.account, ...payload };
    saveDFundedSession(session);
    emitAll({ msg_type: 'balance', balance: fundedBalancePayload() });
};

const handleStreamedTick = (symbol, tick) => {
    const poller = symbolTickPollers.get(symbol);
    if (!poller) return;
    const epoch = Number(tick.epoch);
    lastStreamedTickAt.set(symbol, Date.now());
    if (!(epoch > poller.lastEpoch)) return;
    poller.lastEpoch = epoch;
    const quote = Number(tick.quote);
    emitTick(poller, symbol, {
        epoch,
        quote,
        ask     : tick.ask,
        bid     : tick.bid,
        pip_size: Number(tick.pip_size) || pipDecimalsOf(symbol, [quote]),
    });
};

let gatewayToken = null;
let gatewayUrl;

const ensureGateway = url => {
    const session = getDFundedSession();
    if (!url || !session?.account) {
        gateway?.close();
        gateway = null;
        gatewayToken = null;
        return;
    }
    if (gateway) {
        // Token rotated or the account changed: re-authenticate the open socket.
        if (gatewayToken !== session.accessToken) {
            gatewayToken = session.accessToken;
            gateway.reauthenticate();
        }
        return;
    }
    gatewayToken = session.accessToken;
    gateway = new DFundedGateway(
        url,
        () => {
            const current = getDFundedSession();
            return current?.account ? { token: current.accessToken, accountId: current.account.id } : null;
        },
        {
            onOrder      : payload => handleOrderPush(payload),
            onAccount    : payload => handleAccountPush(payload),
            onTick       : handleStreamedTick,
            onReadyChange: () => {
                // Ticks come from Deriv's public socket; a symbol whose stream has
                // gone quiet asks for the backend's from its own timer.
                wakeOrderWaiters();
            },
            refreshToken: async () => {
                const current = getDFundedSession();
                if (!current) return null;
                try {
                    return (await refreshSession(current)).accessToken;
                } catch {
                    return null;
                }
            },
        }
    );
};

/** Pushes are flowing over either transport, so REST is only a safety net. */
const pushReady = () => realtimeReady || Boolean(gateway?.isReady);

let ensureRealtimeInFlight = null;

const ensureRealtime = () => {
    // Single-flight: every consumer calls this at init, and concurrent runs
    // would each open their own channel → duplicate events.
    if (!ensureRealtimeInFlight) {
        ensureRealtimeInFlight = doEnsureRealtime().finally(() => {
            ensureRealtimeInFlight = null;
        });
    }
    return ensureRealtimeInFlight;
};

const doEnsureRealtime = async () => {
    const accountId = getDFundedSession()?.account?.id;
    ensureGateway(accountId ? gatewayUrl : undefined);
    if (!accountId) {
        realtimeCleanup?.();
        realtimeCleanup = null;
        realtimeAccountId = null;
        return;
    }
    if (realtimeAccountId === accountId && realtimeCleanup) return;
    realtimeCleanup?.();
    realtimeCleanup = null;
    realtimeAccountId = accountId;
    try {
        // Order and account pushes come over the gateway socket (the Supabase
        // broadcast channel newdtrader also listened to was dropped: this site
        // has no Supabase client, and the gateway carries every event). When
        // the gateway is down, the REST polling below carries the session.
        const { gateway: gatewayConfig } = await dfundedRequest('/api/dfunded/config');
        gatewayUrl = gatewayConfig?.url;
        ensureGateway(gatewayUrl);
        realtimeCleanup = () => {
            realtimeReady = false;
        };
    } catch {
        /* no gateway — polling fallback carries the session */
    }
};

if (typeof window !== 'undefined') {
    window.addEventListener(DFUNDED_SESSION_EVENT, () => {
        void ensureRealtime();
    });
}

/** Latest pushed state for an order, merged over a REST-fetched row. */
const withPushedOrder = (orderId, order) => {
    const pushed = pushedOrders.get(orderId);
    return pushed ? { ...order, ...pushed } : order;
};

// ─── Contract synthesis ─────────────────────────────────────────────────────

// The transactions list keys rows on transaction_ids.buy. The real Deriv buy
// transaction id can arrive AFTER the first contract event (receipt persistence
// races the open), and letting the id change mid-stream made every trade grow a
// duplicate "ghost" row. Pin whatever id the first event used per contract.
const pinnedBuyTxnIds = new Map();

const pinnedBuyTxnId = (contractId, candidate) => {
    const pinned = pinnedBuyTxnIds.get(contractId);
    if (pinned !== undefined) return pinned;
    pinnedBuyTxnIds.set(contractId, candidate);
    if (pinnedBuyTxnIds.size > 300) {
        const firstKey = pinnedBuyTxnIds.keys().next().value;
        if (firstKey !== undefined) pinnedBuyTxnIds.delete(firstKey);
    }
    return candidate;
};

const buildFundedContract = (order, buy) => {
    const settled = order.status === 'won' || order.status === 'lost';
    const contractId = Number(order.deriv_contract_id ?? buy.contract_id);
    const buyPrice = Number(order.buy_price ?? buy.buy_price ?? order.stake ?? 0);
    const profit = Number(order.pnl ?? 0);
    const sellPrice = Number(order.sell_price ?? (settled ? buyPrice + profit : 0));
    const snapshot = orderSnapshot(order) || {};
    const receipt = orderReceipt(order);
    const symbol = String(snapshot.symbol || order.symbol || '');
    const entryTick = snapshot.entry_tick ?? snapshot.entry_spot ?? order.entry_tick ?? undefined;
    const entryTickTime = snapshot.entry_tick_time ?? snapshot.entry_spot_time ?? order.entry_tick_time ?? undefined;
    const exitTick = settled
        ? (order.exit_tick ?? snapshot.exit_tick ?? snapshot.sell_spot ?? undefined)
        : (snapshot.exit_tick ?? undefined);
    const exitTickTime = settled
        ? (order.exit_tick_time ?? snapshot.exit_tick_time ?? snapshot.sell_spot_time ?? undefined)
        : (snapshot.exit_tick_time ?? undefined);
    const purchaseTime =
        Number(receipt.purchase_time ?? snapshot.purchase_time ?? snapshot.date_start ?? buy.purchase_time ?? 0) ||
        undefined;

    return {
        // Real mirrored Deriv state first: current_spot, tick_count/tick_passed,
        // barriers, validation flags, date_expiry etc. all flow through when the
        // executor has written a snapshot.
        ...snapshot,
        contract_id    : contractId,
        transaction_ids: {
            buy: pinnedBuyTxnId(
                contractId,
                Number(receipt.buy_transaction_id ?? snapshot.transaction_ids?.buy ?? buy.transaction_id ?? contractId)
            ),
            sell: settled ? Number(snapshot.transaction_ids?.sell ?? contractId) : undefined,
        },
        contract_type           : order.contract_type ?? snapshot.contract_type,
        underlying              : symbol,
        symbol,
        display_name            : snapshot.display_name || symbol,
        barrier                 : order.barrier ?? snapshot.barrier ?? undefined,
        currency                : fundedCurrency(),
        buy_price               : buyPrice,
        bid_price               : settled ? sellPrice : Number(snapshot.bid_price ?? buyPrice),
        sell_price              : settled ? sellPrice : undefined,
        payout                  : Number(receipt.payout ?? snapshot.payout ?? 0) || buyPrice,
        profit                  : settled ? profit : Number(snapshot.profit ?? 0),
        longcode                : receipt.longcode ?? snapshot.longcode ?? undefined,
        shortcode               : receipt.shortcode ?? snapshot.shortcode ?? undefined,
        status                  : settled ? (order.status === 'won' ? 'won' : 'lost') : 'open',
        is_sold                 : settled ? 1 : 0,
        is_expired              : settled ? 1 : Number(snapshot.is_expired ?? 0),
        is_settleable           : settled ? 1 : Number(snapshot.is_settleable ?? 0),
        is_valid_to_sell        : settled ? 0 : Number(snapshot.is_valid_to_sell ?? 0),
        entry_tick              : entryTick,
        entry_spot              : entryTick,
        entry_tick_display_value: entryTick !== undefined ? String(entryTick) : undefined,
        entry_tick_time         : entryTickTime,
        exit_tick               : exitTick,
        sell_spot               : exitTick,
        exit_tick_display_value : exitTick !== undefined ? String(exitTick) : undefined,
        exit_tick_time          : exitTickTime,
        sell_time               : settled ? (exitTickTime ?? undefined) : undefined,
        date_start              : purchaseTime,
        purchase_time           : purchaseTime,
        date_expiry             : snapshot.date_expiry ?? undefined,
        tick_count              : snapshot.tick_count ?? (order.duration_unit === 't' ? order.duration : undefined),
    };
};

/**
 * Poll the funded order while it is open, mirroring the executor's contract
 * snapshots into proposal_open_contract stream messages — the browser-side
 * equivalent of Deriv's POC subscription. Emits the settlement transaction and
 * a balance update when the contract closes.
 */
const streamContract = async (orderId, initialOrder, buy) => {
    if (streamingOrders.has(orderId)) return;
    streamingOrders.add(orderId);
    const contractId = Number(initialOrder.deriv_contract_id ?? buy.contract_id);
    trackedContracts.set(contractId, { orderId, order: initialOrder, buy });

    try {
        emitSoon({
            msg_type   : 'transaction',
            transaction: {
                action        : 'buy',
                contract_id   : contractId,
                transaction_id: buy.transaction_id ?? contractId,
                amount        : -Number(initialOrder.buy_price ?? initialOrder.stake ?? 0),
                currency      : fundedCurrency(),
                balance       : fundedBalancePayload().balance,
                longcode      : buy.longcode,
                symbol        : initialOrder.symbol,
            },
        });
        emitSoon({ msg_type: 'balance', balance: fundedBalancePayload() });

        let lastEmitted = '';
        const deadline = Date.now() + 6 * 60 * 60 * 1000; // 6h hard stop
        let order = withPushedOrder(orderId, initialOrder);
        let lastRestFetch = Date.now();
        let terminalGraceUntil = 0;
        while (Date.now() < deadline) {
            const tracked = { orderId, order, buy };
            trackedContracts.set(contractId, tracked);
            const contract = buildFundedContract(order, buy);
            const fingerprint = JSON.stringify(contract);
            if (fingerprint !== lastEmitted) {
                lastEmitted = fingerprint;
                emitSoon({
                    msg_type              : 'proposal_open_contract',
                    proposal_open_contract: contract,
                    subscription          : { id: `dfunded-poc-${contractId}` },
                });
            }
            if (terminalOrderStatuses.has(order.status)) {
                // Deriv's first "sold" update can precede the exit-tick fields.
                // Linger briefly so the enriched update still reaches the row —
                // otherwise the exit spot stays a loader forever.
                if (contract.exit_tick !== undefined && contract.exit_tick !== null) break;
                // Kept short: nothing can be bought again until this loop ends, and
                // the executor now sends the exit spot with the close itself.
                if (!terminalGraceUntil) terminalGraceUntil = Date.now() + 1200;
                if (Date.now() > terminalGraceUntil) break;
            }

            // Push-first: wake instantly on a broadcast; otherwise tick over on a
            // timer. REST refresh runs continuously only when realtime is down —
            // with pushes flowing it is just a slow safety net.
            const idleMs = pushReady() ? 1500 : 400;
            await Promise.race([orderPushSignal(), new Promise(resolve => setTimeout(resolve, idleMs))]);
            order = withPushedOrder(orderId, order);
            // During the terminal grace window a REST refetch is still useful
            // when realtime is down — the delayed settle writes the exit ticks.
            // A closed contract still missing its exit spot gets one prompt REST
            // look instead of waiting on a push that may never come.
            const awaitingExit = terminalOrderStatuses.has(order.status);
            if (awaitingExit ? Date.now() - lastRestFetch > 600 : !pushReady() || Date.now() - lastRestFetch > 5000) {
                lastRestFetch = Date.now();
                const next = await getDFundedOrder(orderId).catch(() => null);
                if (next?.order) order = withPushedOrder(orderId, next.order);
            }
        }

        if (order.status === 'won' || order.status === 'lost') {
            // Emit immediately — the executor's 'account' push delivers the new
            // balance in parallel; the REST refresh is only a background safety
            // net and must not delay the visible sell.
            emitSoon({
                msg_type   : 'transaction',
                transaction: {
                    action        : 'sell',
                    contract_id   : contractId,
                    transaction_id: contractId,
                    amount        : Number(order.sell_price ?? 0),
                    currency      : fundedCurrency(),
                    balance       : fundedBalancePayload().balance,
                },
            });
            // With realtime up, the executor's 'account' push delivers the exact
            // post-settlement balance moments later — emitting the stale local
            // figure first just makes the balance visibly wobble.
            if (!pushReady()) emitSoon({ msg_type: 'balance', balance: fundedBalancePayload() });
            void refreshDFundedAccount().catch(() => null);
        }
    } catch (error) {
        emitSoon({
            error: { code: 'DFundedSettlementError', message: error instanceof Error ? error.message : String(error) },
        });
    } finally {
        streamingOrders.delete(orderId);
    }
};

/** Rebuild open-contract tracking after a page reload so portfolio requests,
 * contract cards and settlement events survive a refresh mid-contract. */
const resumeOpenContracts = async () => {
    if (hydrated) return;
    hydrated = true;
    try {
        const { orders } = await getDFundedOrders('open');
        for (const order of orders || []) {
            if (!order.deriv_contract_id) continue;
            const receipt = orderReceipt(order);
            const buy = {
                contract_id   : Number(order.deriv_contract_id),
                transaction_id: Number(receipt.buy_transaction_id ?? order.deriv_contract_id),
                buy_price     : Number(order.buy_price ?? order.stake ?? 0),
                longcode      : receipt.longcode,
            };
            void streamContract(order.id, order, buy);
        }
    } catch {
        /* hydration is best-effort; polling resumes on next action */
    }
};

// ─── Statement / profit table synthesis ─────────────────────────────────────

const settledOrderRows = async () => {
    const { orders } = await getDFundedOrders();
    return (orders || []).filter(order => order.status === 'won' || order.status === 'lost');
};

// Reports ask for pages (limit/offset) and date ranges. Answering every request
// with the full list made "load more" append the same rows again, and rows
// without a shortcode render with no trade icon or name.
const settleTimeOf = order => {
    const settledAt = order.settled_at ? Math.floor(new Date(order.settled_at).getTime() / 1000) : 0;
    return Number(order.exit_tick_time ?? 0) || settledAt;
};

const pageOf = (req, rows, timeOf) => {
    const from = Number(req.date_from) || 0;
    const to = Number(req.date_to) || Number.MAX_SAFE_INTEGER;
    const inRange = rows.filter(row => timeOf(row) >= from && timeOf(row) <= to);
    const ordered = inRange.sort((x, y) => (req.sort === 'ASC' ? timeOf(x) - timeOf(y) : timeOf(y) - timeOf(x)));
    const offset = Math.max(0, Number(req.offset) || 0);
    const limit = Math.max(1, Number(req.limit) || 50);
    return ordered.slice(offset, offset + limit);
};

const synthesizeStatement = async req => {
    const rows = (await settledOrderRows().catch(() => [])).sort((x, y) => settleTimeOf(y) - settleTimeOf(x));
    // Walk back from today's balance so each row shows the balance after it,
    // the way a Deriv statement does.
    let running = Number(getDFundedSession()?.account?.current_equity ?? 0);
    const transactions = rows.flatMap(order => {
        const receipt = orderReceipt(order);
        const contractId = Number(order.deriv_contract_id ?? 0);
        const buyTransactionId = Number(receipt.buy_transaction_id ?? contractId);
        const sellPrice = Number(order.sell_price ?? 0);
        const buyPrice = Number(order.buy_price ?? 0);
        const common = {
            contract_id  : contractId,
            longcode     : receipt.longcode ?? '',
            shortcode    : receipt.shortcode ?? '',
            payout       : Number(receipt.payout ?? 0),
            purchase_time: Number(receipt.purchase_time ?? 0),
            app_id       : null,
        };
        const afterSell = running;
        const afterBuy = running - sellPrice;
        running = afterBuy + buyPrice;
        return [
            {
                ...common,
                action_type     : 'sell',
                amount          : sellPrice,
                balance_after   : Number(afterSell.toFixed(2)),
                transaction_id  : contractId,
                reference_id    : buyTransactionId,
                transaction_time: settleTimeOf(order),
            },
            {
                ...common,
                action_type     : 'buy',
                amount          : -buyPrice,
                balance_after   : Number(afterBuy.toFixed(2)),
                transaction_id  : buyTransactionId,
                reference_id    : null,
                transaction_time: Number(receipt.purchase_time ?? 0),
            },
        ];
    });
    const wanted = req.action_type ? transactions.filter(row => row.action_type === req.action_type) : transactions;
    const page = pageOf(req, wanted, row => row.transaction_time);
    return { echo_req: req, msg_type: 'statement', statement: { count: page.length, transactions: page } };
};

const synthesizeProfitTable = async req => {
    const rows = await settledOrderRows().catch(() => []);
    const transactions = rows.map(order => {
        const receipt = orderReceipt(order);
        return {
            app_id           : null,
            contract_id      : Number(order.deriv_contract_id ?? 0),
            transaction_id   : Number(receipt.buy_transaction_id ?? order.deriv_contract_id ?? 0),
            buy_price        : Number(order.buy_price ?? 0),
            sell_price       : Number(order.sell_price ?? 0),
            payout           : Number(receipt.payout ?? 0),
            profit           : Number(order.pnl ?? 0),
            purchase_time    : Number(receipt.purchase_time ?? 0),
            sell_time        : settleTimeOf(order),
            underlying_symbol: order.symbol,
            symbol           : order.symbol,
            contract_type    : order.contract_type,
            longcode         : receipt.longcode ?? '',
            shortcode        : receipt.shortcode ?? '',
            duration_type    : order.duration_unit,
        };
    });
    const page = pageOf(req, transactions, row => row.sell_time);
    return { echo_req: req, msg_type: 'profit_table', profit_table: { count: page.length, transactions: page } };
};

// ─── Debug visibility (state only, no secrets) ──────────────────────────────
// Smaller than MPS's `__dfundedAdapterDebug` — no listener count / tracked
// contract list here since the emitter and contract map are transport-owned
// concerns now; just enough to see the tick pollers and realtime status.

if (typeof window !== 'undefined') {
    window.__dfundedAdapterDebug = {
        get tickPollers() {
            return [...symbolTickPollers.entries()].map(([symbol, poller]) => ({
                symbol,
                subs        : [...poller.subscriptionIds],
                lastEpoch   : poller.lastEpoch,
                backoffUntil: poller.backoffUntil,
            }));
        },
        get realtimeReady() {
            return pushReady();
        },
    };
}

module.exports = {
    DFUNDED_SESSION_EVENT,
    getDFundedSession,
    isDFundedOfferedOnSite,
    getDFundedAccountType,
    getDFundedProgramName,
    loginDFunded,
    logoutDFunded,
    saveDFundedSession,
    isDFundedMode,
    getDFundedApiBase,
    setDFundedEmitter,
    dfundedRequest,
    marketRequest,
    executeDFundedPurchase,
    sellDFundedContract,
    getDFundedOrder,
    getDFundedOrders,
    waitForDFundedOrder,
    streamContract,
    resumeOpenContracts,
    ensureRealtime,
    joinTickStream,
    emitTick,
    stopTickPoller,
    stopAllTickPollers,
    buildFundedContract,
    synthesizeStatement,
    synthesizeProfitTable,
    orderSnapshot,
    orderReceipt,
    handleProposal,
    stopProposalPoller,
    stopAllProposalPollers,
    proposals,
    trackedContracts,
    terminalOrderStatuses,
    refreshDFundedAccount,
    pollTicksHistory,
    pipDecimalsOf,
};
